import type { MouseEvent } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';

import { PictoMoto } from '@/assets/icons-april-fr/pictos';
import SimpleDatePicker from '@/components/forms/simple-date-picker';
import Text from '@/components/forms/text';
import { AUTH_GRAVITEE_ENABLED, HOST_MB } from '@/lib/constants';
import { useForgotEmail } from '@/hooks/auth/use-auth-recovery';
import usePageTitle from '@/hooks/use-page-title';
import { oubliEmailSchema, type OubliEmailValues } from '@/schemas/oubli-email';
import { initiateGraviteeLogin } from '@/lib/auth/gravitee';

const OubliEmail = () => {
    const forgotEmail = useForgotEmail();
    const host = HOST_MB;
    usePageTitle('Email oublié');

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<OubliEmailValues>({
        defaultValues: {},
        resolver: zodResolver(oubliEmailSchema) as unknown as Resolver<OubliEmailValues>,
    });

    const handleBack = (e: MouseEvent<HTMLAnchorElement>) => {
        if (AUTH_GRAVITEE_ENABLED) {
            e.preventDefault()
            void initiateGraviteeLogin()
        }
    }

    const showToolTip = () => {
        void Swal.fire({
            imageUrl: '/images/login/avi-echeance.png',
            confirmButtonText: 'J\'ai compris'
        })
    }

    return (
        <form onSubmit={handleSubmit(values => forgotEmail.mutate(values))}>
            <h1 className={'mb-6 font-bold'} style={{fontSize: '1.75rem'}}><PictoMoto style={{width: 42, height: 42, verticalAlign: 'middle', display: 'inline-block', marginRight: 8}} />Je ne me souviens plus de mon <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.85)_c_h)]'}>email de connexion</span></h1>

            <div className={'mb-6'}>
                <Text
                    type={'text'}
                    autoComplete="family-name"
                    {...register('nom')}
                    value={watch('nom')}
                    required
                    label={'Votre nom de famille'}
                />
            </div>

            <div className={'mb-2'}>
                <Text
                    type={'number'}
                    {...register('belair_id')}
                    value={watch('belair_id')}
                    error={errors.belair_id?.message}
                    required
                    label={'Votre numéro client'}
                />

                { host === 43397 && <button type="button" className={'font-bold inline-block mt-2 mb-4 text-primary cursor-pointer bg-transparent border-0 p-0'} onClick={() => showToolTip()}>Où trouver mon numéro client ?</button> }
            </div>

            <div className={'mb-6'}>
                <SimpleDatePicker
                    label={'Votre date de naissance'}
                    name={'date_naissance'}
                    setValue={(field, value) => setValue(field as 'date_naissance', value)}
                    value={watch('date_naissance')}
                    error={errors.date_naissance?.message}
                    required
                />
            </div>

            <div className={'mb-6'}>
                <Text
                    type={'text'}
                    autoComplete="email"
                    {...register('email')}
                    value={watch('email')}
                    required
                    label={'Votre adresse mail personnelle'}
                />
            </div>

            <div className="grid gap-2">
                <button type="submit" className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover'}>
                    Je valide mon adresse email
                </button>
                <Link to={'/login'} onClick={handleBack} className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-transparent text-primary border border-primary hover:bg-transparent hover:text-foreground hover:border-foreground mt-6'}>
                    Retourner à la page de connexion
                </Link>
            </div>

        </form>
    );
};

export default OubliEmail;
